<script setup lang="ts">
import { computed } from "vue";
import { UiText, UiAvatar, UiIcon, UiNavLink, generateInitials, getExpertTypeDisplay } from "@shopexperts/shared";
import type { ClientInboxProject } from "@/v2Projects/types";
import QuoteDetailStrip from "./QuoteDetailStrip.vue";
import ProjectOpenLinkLabel from "./ProjectOpenLinkLabel.vue";
import {
  getStatusConfig,
  getPartnerTierLabel,
  formatRelativeTime,
  resolveLocationDisplay,
} from "@/v2Projects/utils/display";

const props = withDefaults(defineProps<{
  project: ClientInboxProject;
  compact?: boolean;
  linked?: boolean;
}>(), {
  compact: false,
  linked: true,
});

const status = computed(() => getStatusConfig(props.project.project_status, props.project.project_type));
const projectRoute = computed(() => ({
  name: "client-project-details",
  params: { id: String(props.project.id) },
}));
const rootComponent = computed(() => props.linked ? UiNavLink : "div");
const rootProps = computed(() => props.linked ? { to: projectRoute.value } : {});

const expert = computed(() => props.project.expert);
const expertName = computed(() => expert.value?.name ?? "Unassigned");
const expertInitials = computed(() => generateInitials(expertName.value));

const expertAvatarUrl = computed(() => {
  const e = expert.value;
  if (!e) return undefined;
  return e.avatar ?? e.profile_picture_media?.cdn_url ?? undefined;
});

const expertTypeLabel = computed(() => {
  const e = expert.value;
  if (!e) return "";
  return getExpertTypeDisplay(e);
});

const expertLocation = computed(() => {
  const raw = expert.value?.location?.countryAndCity;
  if (!raw) return null;
  return resolveLocationDisplay(raw);
});

const expertRating = computed(() => {
  const e = expert.value;
  if (!e?.reviews) return null;
  if (e.reviews.count === 0) return null;
  return {
    average: e.reviews.average_rating.toFixed(1),
    count: e.reviews.count,
  };
});

const partnerTier = computed(() =>
  getPartnerTierLabel(expert.value?.partnerTier),
);

const activeQuote = computed(() => props.project.latest_active_quote ?? null);

const lastMessage = computed(() => {
  const lm = props.project.last_message;
  if (!lm) return null;
  const senderName = lm.user
    ? [lm.user.first_name, lm.user.last_name].filter(Boolean).join(" ") || lm.user.email
    : lm.org?.name;
  return {
    text: lm.excerpt ?? "",
    sender: senderName ?? "Someone",
    time: formatRelativeTime(lm.sent_at),
  };
});
</script>

<template>
  <component
    :is="rootComponent"
    v-bind="rootProps"
    class="flex items-start gap-3 px-5 py-3 border-l-[3px] border-l-transparent cursor-pointer transition-colors duration-150 hover:bg-gray-50 group/ui-button no-underline text-inherit"
    :class="[status.accentBorder, { 'py-2': compact }]"
  >
    <!-- Col 1: Avatar -->
    <div
      class="shrink-0 rounded-full bg-white p-[2px] ring-2"
      :class="status.avatarRing"
    >
      <UiAvatar
        :url="expertAvatarUrl"
        :initials="expertInitials"
        :name="expertName"
        size="xs"
        :responsive="false"
        color-coded
      />
    </div>

    <!-- Col 2: Expert info -->
    <div class="min-w-0 flex-1 lg:flex-initial lg:min-w-64">
      <!-- Name + PRO + Status -->
      <div class="flex items-center gap-2 min-w-0 flex-wrap">
        <UiText as="span" variant="p-small" class="font-semibold text-gray-900 truncate">
          {{ expertName }}
        </UiText>
        <span
          v-if="expert?.isPro"
          class="inline-flex items-center px-1.5 py-px rounded-sm text-[9px] font-bold leading-4 bg-gray-900 text-white tracking-wider shrink-0"
        >
          PRO
        </span>
        <span
          :class="status.badgeClass"
          class="inline-flex items-center px-2 py-0.5 rounded-sm text-[11px] font-semibold leading-4 tracking-wide shrink-0"
        >
          {{ status.label }}
        </span>
      </div>

      <!-- Type/role + location -->
      <div class="flex items-center gap-1.5 mt-0.5 flex-wrap">
        <UiText v-if="expertTypeLabel" as="span" variant="p-small" class="truncate max-w-48 text-gray-600">
          {{ expertTypeLabel }}
        </UiText>
        <template v-if="expertLocation">
          <span class="text-gray-400">&middot;</span>
          <UiText as="span" variant="p-small" class="inline-flex items-center gap-0.5 shrink-0 text-gray-600">
            <UiIcon name="map-pin" class="w-3 h-3 text-gray-500" />
            {{ expertLocation }}
          </UiText>
        </template>
      </div>

      <!-- Rating + partner tier -->
      <div v-if="expertRating || partnerTier" class="flex items-center gap-2 mt-1 flex-wrap">
        <div v-if="expertRating" class="flex items-center gap-1">
          <UiIcon name="dynamic-star" class="w-4 h-4 text-se-green" />
          <UiText as="span" variant="p-small" class="font-semibold text-gray-900">
            {{ expertRating.average }}
          </UiText>
          <UiText as="span" variant="p-small" class="text-gray-500">
            ({{ expertRating.count }} reviews)
          </UiText>
        </div>
        <template v-if="partnerTier">
          <span v-if="expertRating" class="text-gray-400 text-xs">&middot;</span>
          <UiText as="span" variant="p-small" class="shrink-0 text-emerald-600 font-medium">
            {{ partnerTier }}
          </UiText>
        </template>
      </div>

      <!-- Last message or description (below expert details) -->
      <div v-if="lastMessage" class="flex items-center gap-1.5 mt-1.5">
        <UiIcon name="chat-bubble" class="w-3 h-3 text-gray-500 shrink-0" />
        <UiText as="span" variant="p-small" class="text-gray-600 truncate min-w-0">
          <span class="font-medium text-gray-800">{{ lastMessage.sender }}:</span>
          {{ lastMessage.text }}
        </UiText>
        <UiText as="span" variant="p-small" class="text-gray-500 shrink-0">
          &middot; {{ lastMessage.time }}
        </UiText>
      </div>
      <UiText v-else-if="project.description" as="p" variant="p-small" class="mt-1.5 line-clamp-1 text-gray-600 min-w-0">
        {{ project.description }}
      </UiText>
    </div>

    <div class="hidden md:flex ml-auto items-center self-center shrink-0">
      <!-- Col 3: Quote details -->
      <div v-if="activeQuote" class="hidden lg:flex shrink-0 mr-8 xl:mr-12">
        <QuoteDetailStrip :quote="activeQuote" />
      </div>

      <!-- Col 4: Action -->
      <div class="shrink-0">
        <ProjectOpenLinkLabel />
      </div>
    </div>
  </component>
</template>
